/* React hook that connects to the WebSocket and dispatches events to a callback.
 * Also exposes a small WS controller so callers can `send` arbitrary actions
 * (e.g. streaming AI requests) and observe the connection status.
 *
 * Usage:
 *   const { send, status } = useLiveUpdates(topics, (event, payload) => {...});
 *   send({action: 'ai_explain', port_call_id: id});
 *
 * status is one of: 'connecting' | 'open' | 'reconnecting' | 'closed'
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { getAccessToken } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function wsUrlFor(backend) {
  if (!backend) return null;
  if (backend.startsWith("https://")) return backend.replace(/^https:/, "wss:") + "/api/ws";
  if (backend.startsWith("http://")) return backend.replace(/^http:/, "ws:") + "/api/ws";
  return backend + "/api/ws";
}

function protocolToken(token) {
  const bytes = new TextEncoder().encode(token);
  let binary = "";
  bytes.forEach(value => { binary += String.fromCharCode(value); });
  return `bearer.${btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")}`;
}

export default function useLiveUpdates(topics, onEvent) {
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const closedManually = useRef(false);
  const topicsRef = useRef(topics);
  const onEventRef = useRef(onEvent);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const [status, setStatus] = useState("connecting");

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { topicsRef.current = topics; }, [topics]);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(typeof msg === "string" ? msg : JSON.stringify(msg));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    const url = wsUrlFor(BACKEND_URL);
    if (!url) return;
    closedManually.current = false;

    const clearTimers = () => {
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    };

    const connect = async () => {
      let ws;
      try {
        setStatus(reconnectAttempts.current > 0 ? "reconnecting" : "connecting");
        const token = await getAccessToken();
        const protocols = ["portcall.v1"];
        if (token) protocols.push(protocolToken(token));
        ws = new WebSocket(url, protocols);
      } catch (e) { return; }
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempts.current = 0;
        setStatus("open");
        ws.send(JSON.stringify({ action: "subscribe", topics: topicsRef.current }));
        if (pingTimerRef.current) clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: "ping" }));
        }, 30000);
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.event === "pong" || msg.event === "ready" || msg.event === "subscribed") return;
          if (msg.event && onEventRef.current) {
            onEventRef.current(msg.event, msg.payload, msg.topic, msg);
          }
        } catch (_) {}
      };

      ws.onclose = () => {
        if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
        if (closedManually.current) { setStatus("closed"); return; }
        setStatus("reconnecting");
        const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempts.current));
        reconnectAttempts.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws.close(); } catch (_) {}
      };
    };

    connect();
    return () => {
      closedManually.current = true;
      clearTimers();
      try { wsRef.current?.close(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics.join("|")]);

  return { send, status };
}
